// components/MapComponent.js - Componente de mapa reutilizable con branding actualizado
import React from 'react';
import { useTecnicoColores } from '../hooks/useTecnicos';

const MapComponent = ({ center, markers, clientMarker, userLocation, loading, showLegend = true }) => {
  const tecnicoColors = useTecnicoColores();
  const mapRef = React.useRef(null);
  const mapInstanceRef = React.useRef(null);
  const markersRef = React.useRef([]);
  const [mapReady, setMapReady] = React.useState(false);

  // Inicializar Google Maps
  React.useEffect(() => {
    if (mapInstanceRef.current || !mapRef.current) return;

    const loadGoogleMaps = () => {
      // Verificar si ya existe la API
      if (window.google && window.google.maps) {
        initMap();
        return;
      }

      // Cargar Google Maps
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCtcSBSMOJcok9G5ddvQ3fsjmRGV8-lmVM&libraries=geometry`;
      script.async = true;
      script.defer = true;
      script.onload = initMap;
      script.onerror = () => {
        console.error("Error cargando Google Maps, usando mapa simulado");
        setMapReady(true); // Activar modo fallback
      };
      document.head.appendChild(script);
    };

    const initMap = () => {
      try {
        if (!window.google || !mapRef.current) return;

        console.log("🗺️ Inicializando Google Maps");

        const map = new window.google.maps.Map(mapRef.current, {
          center: { lat: center.lat, lng: center.lng },
          zoom: 13,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
          styles: [
            {
              featureType: "poi",
              elementType: "labels",
              stylers: [{ visibility: "off" }]
            }
          ]
        });

        mapInstanceRef.current = map;
        setMapReady(true);
        console.log("✅ Google Maps inicializado");
      } catch (error) {
        console.error("❌ Error inicializando Google Maps:", error);
        setMapReady(true); // Activar fallback
      }
    };

    loadGoogleMaps();

    return () => {
      // Limpiar marcadores
      markersRef.current.forEach(marker => {
        if (marker.setMap) marker.setMap(null);
      });
      markersRef.current = [];
    };
  }, []);

  // Actualizar marcadores
  React.useEffect(() => {
    if (!mapReady) return;

    // Si Google Maps no está disponible, usar mapa simulado
    if (!window.google || !window.google.maps || !mapInstanceRef.current) {
      console.log("📍 Usando mapa simulado");
      return;
    }

    try {
      console.log("🔄 Actualizando marcadores Google Maps");

      // Limpiar marcadores existentes
      markersRef.current.forEach(marker => {
        marker.setMap(null);
      });
      markersRef.current = [];

      const bounds = new window.google.maps.LatLngBounds();

      // Añadir marcador del cliente
      if (clientMarker && clientMarker.lat && clientMarker.lng) {
        const clientMarker_gm = new window.google.maps.Marker({
          position: { lat: clientMarker.lat, lng: clientMarker.lng },
          map: mapInstanceRef.current,
          title: `Cliente - CP: ${clientMarker.cp}`,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 12,
            fillColor: tecnicoColors.Cliente || "#DC2626",
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3
          }
        });

        const clientInfoWindow = new window.google.maps.InfoWindow({
          content: `
            <div style="text-align: center;">
              <strong style="color: ${tecnicoColors.Cliente || "#DC2626"};">🏠 Cliente</strong><br>
              Código Postal: ${clientMarker.cp}
            </div>
          `
        });

        clientMarker_gm.addListener('click', () => {
          clientInfoWindow.open(mapInstanceRef.current, clientMarker_gm);
        });

        markersRef.current.push(clientMarker_gm);
        bounds.extend({ lat: clientMarker.lat, lng: clientMarker.lng });
        console.log(`📍 Cliente Google Maps: ${clientMarker.lat}, ${clientMarker.lng}`);
      }

      // Añadir marcador de ubicación actual del técnico
      if (userLocation?.lat && userLocation?.lng) {
        const userMarker = new window.google.maps.Marker({
          position: { lat: userLocation.lat, lng: userLocation.lng },
          map: mapInstanceRef.current,
          title: 'Tu ubicación actual',
          zIndex: 1000,
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            scale: 13,
            fillColor: '#2563EB',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          },
        });

        const userInfoWindow = new window.google.maps.InfoWindow({
          content: `<div style="text-align:center;font-size:13px;"><strong style="color:#2563EB;">📍 Tu ubicación</strong></div>`,
        });
        userMarker.addListener('click', () => {
          userInfoWindow.open(mapInstanceRef.current, userMarker);
        });

        markersRef.current.push(userMarker);
        bounds.extend({ lat: userLocation.lat, lng: userLocation.lng });
      }

      // Añadir marcadores de órdenes
      markers.forEach((marker, idx) => {
        if (marker.lat && marker.lng) {
          const orderMarker = new window.google.maps.Marker({
            position: { lat: marker.lat, lng: marker.lng },
            map: mapInstanceRef.current,
            title: `${marker.tecnico} - Orden ${idx + 1}`,
            label: {
              text: (idx + 1).toString(),
              color: 'white',
              fontWeight: 'bold',
              fontSize: '12px'
            },
            icon: {
              path: window.google.maps.SymbolPath.CIRCLE,
              scale: 10,
              fillColor: tecnicoColors[marker.tecnico] || '#6B7280',
              fillOpacity: 1,
              strokeColor: '#ffffff',
              strokeWeight: 2
            }
          });

          const orderInfoWindow = new window.google.maps.InfoWindow({
            content: `
              <div style="text-align: center; max-width: 200px;">
                <strong style="color: ${tecnicoColors[marker.tecnico]};">
                  ${marker.tecnico} - Orden ${idx + 1}
                </strong><br>
                <div style="margin: 5px 0; font-size: 12px;">
                  ${marker.direccion || 'Dirección no disponible'}
                </div>
                ${marker.cp ? `<span style="color: #666;">CP: ${marker.cp}</span>` : ''}
              </div>
            `
          });

          orderMarker.addListener('click', () => {
            orderInfoWindow.open(mapInstanceRef.current, orderMarker);
          });

          markersRef.current.push(orderMarker);
          bounds.extend({ lat: marker.lat, lng: marker.lng });
          console.log(`📍 Orden ${idx + 1} Google Maps: ${marker.lat}, ${marker.lng}`);
        }
      });

      // Ajustar vista
      if (markersRef.current.length > 0) {
        if (markersRef.current.length === 1) {
          mapInstanceRef.current.setCenter(bounds.getCenter());
          mapInstanceRef.current.setZoom(15);
        } else {
          mapInstanceRef.current.fitBounds(bounds);
        }
        console.log(`🎯 Vista ajustada Google Maps para ${markersRef.current.length} marcadores`);
      }

    } catch (error) {
      console.error("❌ Error actualizando Google Maps:", error);
    }
  }, [center, markers, clientMarker, userLocation, mapReady]);

  if (loading) {
    return (
      <div className="relative w-full h-full bg-gray-100 rounded-lg border-2 border-gray-200 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Geocodificando direcciones...</p>
        </div>
      </div>
    );
  }

  // Mapa simulado como fallback si Google Maps no funciona
  if (mapReady && (!window.google || !window.google.maps)) {
    const getMarkerPosition = (index, total) => {
      const cols = Math.ceil(Math.sqrt(total + (clientMarker ? 1 : 0)));
      const row = Math.floor(index / cols);
      const col = index % cols;
      
      return {
        left: `${25 + (col * 50) / Math.max(cols, 1)}%`,
        top: `${25 + (row * 50) / Math.max(Math.ceil((total + (clientMarker ? 1 : 0)) / cols), 1)}%`
      };
    };

    return (
      <div className="relative w-full h-full bg-gradient-to-br from-red-50 via-gray-50 to-slate-50 rounded-lg border-2 border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 bg-white/90 backdrop-blur-sm p-2 border-b border-gray-200 z-10">
          <h3 className="font-medium text-gray-700 text-sm">🗺️ Mapa Simulado - {center.lat.toFixed(4)}, {center.lng.toFixed(4)}</h3>
        </div>

        {/* Área del mapa */}
        <div className="pt-12 p-4 h-full relative">
          {/* Patrón de fondo */}
          <div className="absolute inset-0 opacity-10">
            <div className="w-full h-full grid grid-cols-12 grid-rows-8 gap-px">
              {Array.from({ length: 96 }).map((_, i) => (
                <div key={i} className="bg-gray-400"></div>
              ))}
            </div>
          </div>

          {/* Marcador de ubicación del técnico */}
          {userLocation && (
            <div
              className="absolute transform -translate-x-1/2 -translate-y-1/2 z-30"
              style={{ left: '50%', top: '50%' }}
            >
              <div className="flex flex-col items-center">
                <div className="w-5 h-5 rounded-full bg-blue-600 border-4 border-white shadow-lg animate-pulse" />
                <div className="bg-blue-600 text-white px-2 py-0.5 rounded shadow text-xs mt-1 font-medium whitespace-nowrap">
                  Tú
                </div>
              </div>
            </div>
          )}

          {/* Marcador del cliente */}
          {clientMarker && (
            <div 
              className="absolute transform -translate-x-1/2 -translate-y-1/2 z-20"
              style={{ left: '75%', top: '25%' }}
            >
              <div className="flex flex-col items-center">
                <div 
                  className="w-10 h-10 rounded-full border-4 border-white shadow-lg flex items-center justify-center text-white font-bold animate-pulse"
                  style={{ backgroundColor: tecnicoColors.Cliente || "#DC2626" }}
                >
                  🏠
                </div>
                <div className="bg-white px-2 py-1 rounded shadow text-xs mt-1 font-medium">
                  Cliente CP: {clientMarker.cp}
                </div>
              </div>
            </div>
          )}

          {/* Marcadores de órdenes */}
          {markers.map((marker, idx) => {
            const position = getMarkerPosition(idx, markers.length);
            return (
              <div
                key={idx}
                className="absolute transform -translate-x-1/2 -translate-y-1/2 z-10"
                style={{ left: position.left, top: position.top }}
              >
                <div className="flex flex-col items-center">
                  <div 
                    className="w-8 h-8 rounded-full border-3 border-white shadow-lg flex items-center justify-center text-white font-bold text-sm"
                    style={{ backgroundColor: tecnicoColors[marker.tecnico] || '#6B7280' }}
                    title={marker.direccion}
                  >
                    {idx + 1}
                  </div>
                  <div className="bg-white px-2 py-1 rounded shadow text-xs mt-1 max-w-24 text-center">
                    <div className="font-medium" style={{ color: tecnicoColors[marker.tecnico] }}>
                      {marker.tecnico}
                    </div>
                    <div className="ph-mask text-gray-600 truncate">
                      {marker.direccion?.split(',')[0] || 'Sin dir.'}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {/* Mensaje si no hay datos */}
          {markers.length === 0 && !clientMarker && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-gray-500">
                <div className="text-4xl mb-2">📍</div>
                <p>Selecciona una cita para ver ubicaciones</p>
              </div>
            </div>
          )}
        </div>

        {/* Leyenda */}
        {showLegend && (
          <div className="absolute bottom-4 left-4 bg-white/95 p-3 rounded-lg shadow-lg border z-20">
            <h4 className="font-semibold text-sm mb-2">Leyenda</h4>
            <div className="space-y-1">
              {Object.entries(tecnicoColors).map(([nombre, color]) => (
                <div key={nombre} className="flex items-center gap-2 text-xs">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }}></div>
                  <span>{nombre}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Info */}
        {markers.length > 0 && (
          <div className="absolute bottom-4 right-4 bg-white/95 p-2 rounded-lg shadow border z-20">
            <div className="text-xs text-gray-600">
              📍 {markers.length} ubicaciones
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative w-full h-full rounded-lg overflow-hidden border-2 border-gray-200">
      {/* Contenedor de Google Maps */}
      <div 
        ref={mapRef} 
        className="w-full h-full bg-gray-100"
        style={{ minHeight: '400px' }}
      ></div>
      
      {/* Indicador de carga */}
      {!mapReady && (
        <div className="absolute inset-0 bg-white/90 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Cargando mapa...</p>
          </div>
        </div>
      )}
      
      {/* Leyenda para Google Maps */}
      {showLegend && mapReady && window.google && window.google.maps && (
        <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm p-3 rounded-lg shadow-lg border z-30">
          <h4 className="font-semibold text-sm mb-2 text-gray-800">Leyenda</h4>
          <div className="space-y-1">
            {Object.entries({...tecnicoColors, Cliente: "#DC2626"}).map(([nombre, color]) => (
              <div key={nombre} className="flex items-center gap-2 text-xs">
                <div
                  className="w-3 h-3 rounded-full border border-white"
                  style={{ backgroundColor: color }}
                ></div>
                <span className="text-gray-700">{nombre}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Info de órdenes para Google Maps */}
      {mapReady && window.google && window.google.maps && markers.length > 0 && (
        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm p-2 rounded-lg shadow border z-30">
          <div className="text-xs text-gray-600">
            📍 {markers.length} ubicaciones
          </div>
        </div>
      )}
    </div>
  );
};

export default MapComponent;